import { Slider } from "@mui/material";
import React, { ComponentProps, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Stage } from "../../shared/CaseConfiguration";
import { percentage } from "../../shared/percentage";
import { useCase } from "../../context/CaseContext.tsx";
import {
  hasOpenCosts,
  incurredWithin,
  stageShareOf,
} from "../../shared/costChanges";
import type { Budget, LockedCosts } from "../../shared/CostModel";
import { getColorForCosts } from "../analytics/CostColors";
import { useTheme } from "../../context/ThemeContext";

/**
 * The Cost progress slider: it incurs the costs settlements left open of the
 * stage the timeline has open, what they locked out of its reach (R4 of
 * SettlementFreezeOnSave.test.ts). The stages before stay as closing them
 * left them: dragged beyond the open stage, it asks for it to be closed;
 * dragged back before it, for the stage closed last to be opened again.
 * Where nothing is open, it cannot move.
 */
const CostsPerStageSlider = ({
  budget,
  locked,
  openStage,
  updateBudget,
  onCloseRequested,
  onReopenRequested,
  stepsPerStage = 4,
}: {
  budget: Budget;
  locked: LockedCosts;
  // the stage the timeline has open; none once every stage has ended
  openStage: Stage | undefined;
  updateBudget: (b: Budget) => Promise<void>;
  onCloseRequested: () => void;
  onReopenRequested: () => void;
  stepsPerStage?: 2 | 4;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "costsPerStageSlider" });

  // make sure to use all stages of case
  // unfortunately we need to filter out appeals in budget UI at this moment
  // because of the filtering out, stages after appeals (enforcement) have a different index on slider than their value in enum
  // thus we need to store our own stages array to get the global stage index from the value on the slider
  const caseContext = useCase();
  const stagesOnSlider: Stage[] = caseContext
    .getStages()
    .filter((stage) => stage !== Stage.Appeals);
  // generate mark values for stages that we want to show on slider
  const marks: { value: number }[] = useMemo(
    () =>
      stagesOnSlider
        .map((_, index) =>
          stepsPerStage === 4
            ? [
                { value: index },
                { value: index + 0.25 },
                { value: index + 0.5 },
                { value: index + 0.75 },
              ]
            : [{ value: index }, { value: index + 0.5 }],
        )
        .flat(),
    [stagesOnSlider],
  );

  // max = 100% of last stage
  const max = stagesOnSlider.length;

  const [isSaving, setIsSaving] = useState(false);

  // the open stage on the slider: Appeals is not on it, so where Appeals is
  // open the slider stands at its end and cannot move
  const openIndex =
    openStage === undefined ? -1 : stagesOnSlider.indexOf(openStage);
  // where the open stage's costs are incurred
  const initialSliderPosition = useMemo(
    () =>
      openIndex < 0
        ? openStage === undefined
          ? max
          : stagesOnSlider.filter((stage) => stage < openStage).length
        : openIndex +
          (stageShareOf(budget, locked, openStage!)?.toNumber() ?? 0),
    [budget, locked, openStage],
  );
  // what the slider can reach: the open stage, from none to all of it
  const within = (value: number) =>
    Math.min(Math.max(value, openIndex), openIndex + 1);

  const [sliderValue, setSliderPosition] = useState<number>(
    initialSliderPosition,
  );

  const incurredColors = getColorForCosts("incurred", theme);
  const estimatedColors = getColorForCosts("estimated", theme);
  const isAtEnd = sliderValue >= max;
  const sliderSx: ComponentProps<typeof Slider>["sx"] = {
    "& .MuiSlider-mark": {
      height: theme.spacing(0.5),
      width: theme.spacing(0.5),
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiSlider-track": {
      boxSizing: "border-box",
      backgroundColor: incurredColors.borderColor,
    },
    "& .MuiSlider-rail": {
      boxSizing: "border-box",
      backgroundColor: estimatedColors.borderColor,
    },
    "&.slider-at-end .MuiSlider-thumb[data-index='0'] .MuiSlider-valueLabel.MuiSlider-valueLabelOpen":
      {
        transform: "translateX(-50%) translateY(-100%)",
        "&::before": {
          left: "auto",
          right: "5px",
        },
      },
  };

  const handleCommit = (
    _: React.SyntheticEvent | Event,
    value: number | number[],
  ): Promise<void> => {
    if (typeof value !== "number") return Promise.resolve();

    if (openStage === undefined || openIndex < 0) return Promise.resolve();
    // beyond the open stage: it is to be closed first; before it, the
    // stage closed last opened again
    if (value > openIndex + 1) {
      setSliderPosition(initialSliderPosition);
      onCloseRequested();
      return Promise.resolve();
    }
    if (value < openIndex) {
      setSliderPosition(initialSliderPosition);
      onReopenRequested();
      return Promise.resolve();
    }

    setIsSaving(true);
    const newBudget = incurredWithin(
      budget,
      locked,
      openStage,
      value - openIndex,
    );
    return updateBudget(newBudget)
      .catch((err) => console.error("Failed to update budget:", err))
      .finally(() => setIsSaving(false));
  };

  return (
    <Slider
      value={sliderValue}
      min={0}
      max={max}
      step={1 / stepsPerStage}
      marks={marks}
      valueLabelDisplay="auto"
      valueLabelFormat={(value) => {
        const stageIndex = Math.floor(value);
        const stageProgress = value - stageIndex;
        if (stageProgress === 0 && stageIndex > 0)
          // return 100% of previous stage rather than 0% of current
          return t("sliderLabel", {
            progress: percentage.format(1),
            stage: t(`stages.${Stage[stagesOnSlider[stageIndex - 1]]}`),
          });
        // either progress is greater than 0% or we are at the first stage so 0% is ok
        return t("sliderLabel", {
          progress: percentage.format(stageProgress),
          stage: t(`stages.${Stage[stagesOnSlider[stageIndex]]}`),
        });
      }}
      onChange={(_, value) => {
        if (isSaving) return;
        if (typeof value !== "number") return;
        setSliderPosition(within(value));
      }}
      onChangeCommitted={handleCommit}
      disabled={
        isSaving ||
        caseContext.readOnly ||
        openIndex < 0 ||
        !hasOpenCosts(budget, locked, stagesOnSlider)
      }
      className={isAtEnd ? "slider-at-end" : undefined}
      sx={{
        ...sliderSx,
      }}
    />
  );
};

export default CostsPerStageSlider;
